import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const BLANK = { name: "", phone: "+40", pin: "", language: "ro", truck: "", active: true };

function DriverDialog({ initial, onClose, onSaved }) {
  const [d, setD] = useState(initial ? { ...initial, phone: `+${initial.phone}`, pin: "" } : BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD({ ...d, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: d.name, phone: d.phone, pin: d.pin || null, language: d.language, truck: d.truck, active: d.active };
      if (initial) await api.put(`/drivers/${initial.id}`, body);
      else await api.post("/drivers", body);
      toast.success("Șofer salvat");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  const field = (k, label, props = {}) => (
    <label className="block space-y-1">
      <span className="text-xs text-slate-400">{label}</span>
      <Input value={d[k]} onChange={(e) => set(k, e.target.value)} data-testid={`driver-${k}-input`} {...props} />
    </label>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-white/10 bg-[#111827]" data-testid="driver-dialog">
        <DialogTitle className="font-display text-2xl uppercase">{initial ? "Editează șofer" : "Șofer nou"}</DialogTitle>
        <form onSubmit={submit} className="space-y-4">
          {field("name", "Nume", { required: true })}
          {field("phone", "Telefon WhatsApp (cu prefix, ex. +40722...)", { type: "tel", required: true })}
          {field("pin", initial ? "PIN nou (lasă gol ca să rămână cel vechi)" : "PIN (4-8 cifre)", { inputMode: "numeric", required: !initial })}
          {field("truck", "Camion / nr. înmatriculare")}
          <label className="block space-y-1">
            <span className="text-xs text-slate-400">Limba aplicației și a mesajelor</span>
            <select value={d.language} onChange={(e) => set("language", e.target.value)} data-testid="driver-language-select" className="h-10 w-full rounded-md border border-white/10 bg-[#0A0E17] px-3 text-sm">
              <option value="ro">Română</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </label>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={d.active} onCheckedChange={(v) => set("active", v)} data-testid="driver-active-switch" /> Activ (poate intra în aplicație)
          </label>
          <button type="submit" disabled={busy} data-testid="driver-save-btn" className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-blue-600 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvează
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/drivers").then((r) => setDrivers(r.data));
  useEffect(() => {
    load();
  }, []);
  const remove = async (d) => {
    if (!window.confirm(`Ștergi șoferul ${d.name}? Comenzile lui rămân, dar fără șofer.`)) return;
    await api.delete(`/drivers/${d.id}`);
    toast.success("Șofer șters");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Flotă</div>
          <h1 className="mt-1 font-display text-4xl font-extrabold uppercase sm:text-5xl">Șoferi</h1>
        </div>
        <button onClick={() => setEditing(null)} data-testid="add-driver-btn" className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 font-semibold text-white hover:bg-blue-700">
          <UserPlus className="h-4 w-4" /> Adaugă șofer
        </button>
      </div>
      <p className="mt-3 max-w-2xl text-sm text-slate-400">Șoferul intră pe telefon cu numărul și PIN-ul de aici. Trimite-i link-ul aplicației și PIN-ul o singură dată, apoi poate adăuga aplicația pe ecranul telefonului.</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="drivers-list">
        {drivers.map((d) => (
          <div key={d.id} className={`panel fade-up ${d.active ? "" : "opacity-50"}`} data-testid={`driver-card-${d.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display text-2xl font-bold uppercase">{d.name}</div>
                <div className="font-mono text-xs text-slate-400">+{d.phone}</div>
              </div>
              <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] uppercase">{d.language}</span>
            </div>
            <div className="mt-4 flex items-center gap-3 text-sm text-slate-400">
              <span>{d.truck || "fără camion setat"}</span>
              <span className="ml-auto text-blue-300" data-testid={`driver-active-orders-${d.id}`}>{d.active_orders} curse active</span>
            </div>
            <div className="mt-4 flex gap-2 border-t border-white/5 pt-3">
              <button onClick={() => setEditing(d)} data-testid={`edit-driver-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /> Editează</button>
              <button onClick={() => remove(d)} data-testid={`delete-driver-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /> Șterge</button>
            </div>
          </div>
        ))}
        {!drivers.length && <p className="text-slate-500" data-testid="drivers-empty">Niciun șofer încă. Adaugă primul șofer.</p>}
      </div>
      {editing !== undefined && <DriverDialog initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
